import { Alert, Box, LinearProgress } from "@mui/material";
import { ReactNode, useMemo } from "react";

import { EntityHelper } from "@/common/helpers/entity";
import { renderIf } from "@/common/helpers/render/renderIf";
import { UseApiRequestResult } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { BaseEntityDto, DataGrantPermission, EntityType } from "@/core/api/generated/v1";

import EntityForbiddenAlert from "../../AppAlerts/EntityForbiddenAlert";
import EntityNotFoundAlert from "../../AppAlerts/EntityNotFoundAlert";
import {
  BaseEntityCreateUpdateExtras,
  BaseEntityCreateUpdateExtrasProps,
} from "./BaseEntityCreateUpdateExtras";

export type FormInfoMinimal = {
  /** `true` if form values are not deeply equal from initial values, `false` otherwise.
   * I.e. `isDirty = state !== initial state`
   */
  isDirty: boolean;
};

/** Imperative handle for component that wraps a form. */
export type FormWrapperHandle = {
  formInfo: FormInfoMinimal;
};

export interface BaseEntityCreateUpdateOwnProps<TEntity extends BaseEntityDto> {
  entityType: EntityType;
  entityId: string | null | undefined;
  entity: TEntity | null | undefined;
  /** Entity get request. */
  entityRequest: UseApiRequestResult<any, any, any, any, any> | null | undefined; // use any as we don't care about generic arguments
  /** Whether initialization is in progress and form shouldn't be rendered yet. */
  isIniting?: boolean;
  /** Extra optional features. */
  extrasProps?: BaseEntityCreateUpdateExtrasProps;
  /** Create/update form. */
  children: ReactNode;
}

export type BaseEntityCreateUpdateProps<TEntity extends BaseEntityDto> =
  BaseEntityCreateUpdateOwnProps<TEntity>;

/** Interface to be used by wrapper components that utilize this component. */
export interface BaseEntityCreateUpdateInheritableProps<
  TEntity extends BaseEntityDto,
  TDefaultValues,
> {
  entityId?: string | null;
  defaultValues?: TDefaultValues | null;
  onCreate?: (newValue: TEntity) => void;
  onUpdate?: (newValue: TEntity) => void;
  onSave?: (newValue: TEntity) => void;
  /** When set, cancel button is displayed. */
  onCancel?: () => void;
}

export default function BaseEntityCreateUpdate<TEntity extends BaseEntityDto = BaseEntityDto>({
  entityType,
  entityId,
  entity,
  entityRequest,
  extrasProps,
  isIniting,
  children,
}: BaseEntityCreateUpdateProps<TEntity>) {
  const currentTenant = useCurrentTenant();

  const isCreate = !entityId;
  const isEdit = !!entityId;

  const entityMeta = useMemo(() => EntityHelper.getEntityMeta(entity), [entity]);
  const canEdit = useMemo(
    () =>
      !entityMeta?.grantsMeta ||
      entityMeta.grantsMeta.isIssuedByMe ||
      (entityMeta.grantsMeta.isConsumedByMe &&
        !!currentTenant?.id &&
        !!entityMeta.grantsMeta.tenantPermissionsMap &&
        !!entityMeta.grantsMeta.tenantPermissionsMap[currentTenant.id] &&
        !!entityMeta.grantsMeta.tenantPermissionsMap[currentTenant.id][DataGrantPermission.Write]),
    [entityMeta],
  );

  const isLoading = useMemo(
    () => entityRequest && entityRequest.isLoading && !entity,
    [entityRequest?.isLoading],
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  // ensure crucial data is loaded and init is done before rendering the form
  if (isIniting) {
    return <LinearProgress />;
  }

  if (isEdit && entityRequest) {
    if (entityRequest.responseDetails?.isNotFound && !entity) {
      return <EntityNotFoundAlert />;
    }

    if (
      (entityRequest.responseDetails?.isUnauthorized ||
        entityRequest.responseDetails?.isForbidden) &&
      !entity
    ) {
      return <EntityForbiddenAlert />;
    }

    if (!entity) {
      return null;
    }
  }

  return (
    <Box>
      {extrasProps && <BaseEntityCreateUpdateExtras {...extrasProps} />}

      {renderIf()
        .if(isCreate)
        .then(children)
        .elseif(isEdit)
        .then(
          <Box>
            {!canEdit && <Alert severity='error'>You are not allowed to edit this entity.</Alert>}
            {canEdit && children}
          </Box>,
        )
        .render()}
    </Box>
  );
}
